import { Alert, AlertTitle } from '@/components/ui/alert'
import { AlertCircleIcon, ExternalLinkIcon, ZapIcon } from 'lucide-react'

import { cn } from 'tailwind-variants'

import { ENVIRONMENT } from '@/shared/environment'

/**
 * Inline notice that a feature or limit needs a paid plan, with a pricing link.
 *
 * @remarks
 *   Uses `role="status"`, not the `Alert` default `role="alert"`: these banners
 *   are persistent, so they must not interrupt screen readers on every mount.
 *   Use `variant="destructive"` when the message is an error rather than an
 *   upsell.
 */
export function UpgradeAlert({
  message,
  variant = 'default',
  className,
}: {
  message: string
  variant?: 'default' | 'destructive'
  className?: string
}) {
  const Icon = variant === 'destructive' ? AlertCircleIcon : ZapIcon

  return (
    <Alert
      role="status"
      variant={variant}
      className={cn(
        'bg-transparent',
        variant === 'default' &&
          'border-foreground/15 text-muted-foreground *:[svg]:text-yellow-500',
        variant === 'destructive' && 'border-destructive/30',
        className,
      )}
    >
      <Icon />
      <AlertTitle className="font-normal">
        {message}{' '}
        <a
          href={ENVIRONMENT.marketingLinks.pricing}
          target="_blank"
          rel="noopener noreferrer"
          className="whitespace-nowrap text-foreground"
        >
          See pricing{' '}
          <ExternalLinkIcon className="inline size-3 align-baseline" />
        </a>
      </AlertTitle>
    </Alert>
  )
}
